import React, { Component } from 'react';
import moment from 'moment';
import SectionFooter from '../../components/views/SectionFooter';
import InputField from '../../components/inputs/inputField';
import SelectField from '../../components/inputs/selectField';
import API from '../../utils/API';

const eventTypeOptions = [
	{ value: 'Corporate', label: 'Corporate' },
	{ value: 'Incentive', label: 'Incentive' },
	{ value: 'Association', label: 'Association' },
	{ value: 'Government', label: 'Government' },
	{ value: 'Sports', label: 'Sports' },
	{ value: 'Tour Groups', label: 'Tour Groups' },
	{ value: 'Wedding', label: 'Wedding' },
	{ value: 'Other', label: 'Other' }
];

class EventDetails extends Component {
	constructor(props) {
		super(props);
		const { venue } = props;
		
		this.state = {
			editedVenue: {
				eventDetails: venue.eventDetails || {
					eventName: '',
					eventType: 'Corporate',
					eventTypeOther: '',
					arrivalDate: null,
					departureDate: null,
					alternateDates: [],
					estimatedAttendees: 0,
					roomRateBudget: ''
				}
			},
			editing: false,
			saving: false,
			requiredFields: {
				eventName: false,
				arrivalDate: false,
				departureDate: false
			}
		};
	}

	updateEventDetails = (field, value) => {
		const { editedVenue } = this.state;
		editedVenue.eventDetails[field] = value;
		this.setState({ editedVenue, editing: true });
	};

	addAlternateDate = () => {
		const { editedVenue } = this.state;
		editedVenue.eventDetails.alternateDates.push({
			arrivalDate: null,
			departureDate: null
		});
		this.setState({ editedVenue, editing: true });
	};

	removeAlternateDate = (index) => {
		const { editedVenue } = this.state;
		editedVenue.eventDetails.alternateDates.splice(index, 1);
		this.setState({ editedVenue, editing: true });
	};

	updateAlternateDate = (index, field, value) => {
		const { editedVenue } = this.state;
		editedVenue.eventDetails.alternateDates[index][field] = value;
		this.setState({ editedVenue, editing: true });
	};

	saveDetails = async () => {
		const { editedVenue, requiredFields } = this.state;
		const { eventDetails } = editedVenue;
		
		let valid = true;
		
		if (!eventDetails.eventName) {
			requiredFields.eventName = true;
			valid = false;
		} else {
			requiredFields.eventName = false;
		}
		
		if (!eventDetails.arrivalDate) {
			requiredFields.arrivalDate = true;
			valid = false;
		} else {
			requiredFields.arrivalDate = false;
		}
		
		if (!eventDetails.departureDate) {
			requiredFields.departureDate = true;
			valid = false;
		} else {
			requiredFields.departureDate = false;
		}
		
		if (!valid) {
			this.setState({ requiredFields });
			return;
		}
		
		this.setState({ saving: true });
		
		try {
			await API().patch(
				`Organizations/${this.props.orgId}/sourced-venues/${this.props.venueId}`,
				{ eventDetails: eventDetails }
			);
			
			this.setState({ editing: false, saving: false });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		} catch (error) {
			console.error('Error saving event details:', error);
			this.setState({ saving: false });
		}
	};

	cancelEdit = () => {
		const { venue } = this.props;
		this.setState({
			editedVenue: {
				eventDetails: venue.eventDetails || {
					eventName: '',
					eventType: 'Corporate',
					eventTypeOther: '',
					arrivalDate: null,
					departureDate: null,
					alternateDates: [],
					estimatedAttendees: 0,
					roomRateBudget: ''
				}
			},
			editing: false,
			requiredFields: {
				eventName: false,
				arrivalDate: false,
				departureDate: false
			}
		});
	};

	render() {
		const { editedVenue, editing, requiredFields } = this.state;
		const { venue } = this.props;
		const { eventDetails } = editedVenue;
		
		const arrivalDate = eventDetails.arrivalDate ? moment(eventDetails.arrivalDate).format('YYYY-MM-DD') : '';
		const departureDate = eventDetails.departureDate ? moment(eventDetails.departureDate).format('YYYY-MM-DD') : '';
		
		return (
			<>
				<div className="sectionBody">
					<div className="sectionBlock">
						<h2 className="blockHeader">Basic Event Information</h2>
						
						<InputField
							label="Event Name"
							required={true}
							errorTrigger={requiredFields.eventName}
							value={eventDetails.eventName}
							onChange={(e) => this.updateEventDetails('eventName', e.target.value)}
							placeholder="Enter the name of your event"
						/>
						
						<SelectField
							label="Event Type"
							value={eventTypeOptions.find(opt => opt.value === eventDetails.eventType)}
							options={eventTypeOptions}
							onChange={(option) => this.updateEventDetails('eventType', option.value)}
						/>
						
						{eventDetails.eventType === 'Other' && (
							<InputField
								label="Specify Event Type"
								value={eventDetails.eventTypeOther}
								onChange={(e) => this.updateEventDetails('eventTypeOther', e.target.value)}
								placeholder="Please specify the type of event"
							/>
						)}
						
						<div className="flex wrap" style={{ gap: '15px' }}>
							<div style={{ flex: '1 1 250px' }}>
								<InputField
									label="Arrival Date"
									required={true}
									errorTrigger={requiredFields.arrivalDate}
									type="date"
									value={arrivalDate}
									onChange={(e) => this.updateEventDetails('arrivalDate', e.target.value)}
								/>
							</div>
							<div style={{ flex: '1 1 250px' }}>
								<InputField
									label="Departure Date"
									required={true}
									errorTrigger={requiredFields.departureDate}
									type="date"
									value={departureDate}
									min={arrivalDate}
									onChange={(e) => this.updateEventDetails('departureDate', e.target.value)}
								/>
							</div>
						</div>
						
						<InputField
							label="Estimated Number of Attendees"
							type="number"
							value={eventDetails.estimatedAttendees || ''}
							onChange={(e) => this.updateEventDetails('estimatedAttendees', parseInt(e.target.value) || 0)}
							placeholder="0"
						/>
						
						<InputField
							label="Room Rate Budget/Guidance"
							value={eventDetails.roomRateBudget}
							onChange={(e) => this.updateEventDetails('roomRateBudget', e.target.value)}
							placeholder="e.g., $150-200 per night"
						/>
					</div>
					
					<div className="sectionBlock">
						<div className="flex aic jcsb mb-20">
							<h2 className="blockHeader m-0">Alternate Dates</h2>
							<button className="neu min" onClick={this.addAlternateDate}>
								<i className="las la-plus mr-10" />
								Add Alternate Dates
							</button>
						</div>
						
						{eventDetails.alternateDates && eventDetails.alternateDates.length > 0 ? (
							eventDetails.alternateDates.map((altDate, index) => {
								const altArrival = altDate.arrivalDate ? moment(altDate.arrivalDate).format('YYYY-MM-DD') : '';
								const altDeparture = altDate.departureDate ? moment(altDate.departureDate).format('YYYY-MM-DD') : '';
								
								return (
									<div key={index} className="flex aic mb-15" style={{ gap: '15px' }}>
										<div className="flex wrap" style={{ flex: 1, gap: '15px' }}>
											<div style={{ flex: '1 1 200px' }}>
												<InputField
													label={`Alternate Arrival ${index + 1}`}
													type="date"
													value={altArrival}
													onChange={(e) => this.updateAlternateDate(index, 'arrivalDate', e.target.value)}
												/>
											</div>
											<div style={{ flex: '1 1 200px' }}>
												<InputField
													label={`Alternate Departure ${index + 1}`}
													type="date"
													value={altDeparture}
													min={altArrival}
													onChange={(e) => this.updateAlternateDate(index, 'departureDate', e.target.value)}
												/>
											</div>
										</div>
										<button
											className="p-7 mt-15"
											onClick={() => this.removeAlternateDate(index)}
										>
											<i className="las la-minus-circle" style={{ color: 'red' }} />
										</button>
									</div>
								);
							})
						) : (
							<p className="grey">No alternate dates added yet. Add backup date options if your primary dates are flexible.</p>
						)}
					</div>
				</div>
				
				<SectionFooter
					saveEdit={this.saveDetails}
					cancelEdit={this.cancelEdit}
					updatedAt={venue.updatedAt}
					editing={editing}
				/>
			</>
		);
	}
}

export default EventDetails;
